import React from "react";
import { AlertTriangle, Info, Calendar, Wrench } from "lucide-react";
import type { KumaIncident, KumaMaintenance } from "../types";
import { formatHungarianDateTime } from "../utils";

interface IncidentFeedProps {
  incident: KumaIncident | null;
  maintenanceList: KumaMaintenance[];
}

export const IncidentFeed: React.FC<IncidentFeedProps> = ({
  incident,
  maintenanceList,
}) => {
  const activeMaintenances = maintenanceList.filter((m) => m.active);

  if (!incident && activeMaintenances.length === 0) {
    return (
      <div className="bg-white border border-[#dedbd4] rounded-[0.375rem] p-4 flex items-center gap-3.5">
        <div className="flex-shrink-0 w-8 h-8 rounded-[0.375rem] bg-[#f3f1ec] border border-[#dedbd4] flex items-center justify-center">
          <Info className="w-4 h-4 text-[#62666b]" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-[#1b2a47]">
            Nincs közzétett incidensközlemény
          </h4>
          <p className="text-xs text-[#62666b]">
            A szolgáltatások aktuális állapotát a fenti összesítés és az alábbi rendszerlista mutatja.
          </p>
        </div>
      </div>
    );
  }

  const getIncidentStyles = (style: string) => {
    switch (style) {
      case "danger":
        return {
          border: "border-[#f0bec2]",
          bg: "bg-[#fae9ea]",
          text: "text-[#922d35]",
          badge: "bg-white text-[#922d35] border border-[#f0bec2]",
          icon: AlertTriangle,
          iconColor: "text-[#922d35]",
        };
      case "warning":
        return {
          border: "border-[#edd6a4]",
          bg: "bg-[#faf0d9]",
          text: "text-[#775219]",
          badge: "bg-white text-[#775219] border border-[#edd6a4]",
          icon: AlertTriangle,
          iconColor: "text-[#775219]",
        };
      default:
        return {
          border: "border-[#c6d8e6]",
          bg: "bg-[#eaf0f5]",
          text: "text-[#365570]",
          badge: "bg-white text-[#365570] border border-[#c6d8e6]",
          icon: Info,
          iconColor: "text-[#365570]",
        };
    }
  };

  return (
    <div className="space-y-3">
      {/* Active Incident */}
      {incident && (() => {
        const styles = getIncidentStyles(incident.style);
        const Icon = styles.icon;
        return (
          <div
            className={`rounded-[0.375rem] border p-4 sm:p-5 ${styles.border} ${styles.bg}`}
          >
            <div className="flex items-start gap-3.5">
              <div className="flex-shrink-0 mt-0.5">
                <Icon className={`w-5 h-5 ${styles.iconColor}`} />
              </div>
              <div className="flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-[0.25rem] uppercase tracking-wider ${styles.badge}`}
                    >
                      Aktív incidens
                    </span>
                    <h4 className="text-base font-serif font-semibold text-[#1b2a47]">
                      {incident.title}
                    </h4>
                  </div>
                  <span className="text-xs text-[#62666b] font-mono">
                    Kezdete: {formatHungarianDateTime(incident.createdDate)}
                  </span>
                </div>

                <p className="text-sm text-[#1b2a47] leading-relaxed whitespace-pre-line">
                  {incident.content}
                </p>

                {incident.lastUpdatedDate && (
                  <p className="text-xs text-[#62666b] pt-1">
                    Utolsó frissítés:{" "}
                    <span className="font-mono font-medium text-[#1b2a47]">
                      {formatHungarianDateTime(incident.lastUpdatedDate)}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Active Maintenances */}
      {activeMaintenances.map((m) => (
        <div
          key={m.id}
          className="rounded-[0.375rem] border border-[#c6d8e6] bg-[#eaf0f5] p-4 sm:p-5"
        >
          <div className="flex items-start gap-3.5">
            <div className="flex-shrink-0 mt-0.5">
              <Wrench className="w-5 h-5 text-[#365570]" />
            </div>
            <div className="flex-1 space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-[0.25rem] bg-white text-[#365570] border border-[#c6d8e6] uppercase tracking-wider">
                    Karbantartás
                  </span>
                  <h4 className="text-base font-serif font-semibold text-[#1b2a47]">
                    {m.title}
                  </h4>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-[#62666b] font-mono">
                  <Calendar className="w-3.5 h-3.5 text-[#8c8982]" />
                  <span>
                    {formatHungarianDateTime(m.start)} –{" "}
                    {formatHungarianDateTime(m.end)}
                  </span>
                </div>
              </div>

              <p className="text-sm text-[#1b2a47] leading-relaxed whitespace-pre-line">
                {m.description}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
